import apiClient from '@/shared/lib/api'
import type { FullSummaryResponse, CountrySummary, ProductSummary, OverallPerformance, TrafficSourceSummary, HourlySummaryResponse } from './types'

const SUMMARY_BASE = '/summary'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

interface CountriesResponse {
  countries: CountrySummary[]
  meta: { total: number; start_date: string; end_date: string }
}

interface ProductsResponse {
  products: ProductSummary[]
  meta: { total: number; country?: string; start_date: string; end_date: string }
}

interface OverallResponse {
  overall: OverallPerformance
  traffic_sources: TrafficSourceSummary[]
  meta: { country?: string; start_date: string; end_date: string }
}

export const summaryApi = {
  // Get full summary (all data in one call)
  async getFullSummary(
    startDate: string,
    endDate: string,
    country?: string
  ): Promise<ApiResponse<FullSummaryResponse>> {
    try {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
      }
      if (country) params.country = country

      const response = await apiClient.get<FullSummaryResponse>(SUMMARY_BASE, { params })
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching summary:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch summary' }
    }
  },

  // Get countries summary
  async getCountries(startDate: string, endDate: string): Promise<ApiResponse<CountriesResponse>> {
    try {
      const response = await apiClient.get<CountriesResponse>(`${SUMMARY_BASE}/countries`, {
        params: { start_date: startDate, end_date: endDate },
      })
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching countries:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch countries' }
    }
  },

  // Get products summary (optionally filtered by country)
  async getProducts(
    startDate: string,
    endDate: string,
    country?: string
  ): Promise<ApiResponse<ProductsResponse>> {
    try {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
      }
      if (country) params.country = country

      const response = await apiClient.get<ProductsResponse>(`${SUMMARY_BASE}/products`, { params })
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching products:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch products' }
    }
  },

  // Get overall performance (optionally filtered by country)
  async getOverall(
    startDate: string,
    endDate: string,
    country?: string
  ): Promise<ApiResponse<OverallResponse>> {
    try {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
      }
      if (country) params.country = country

      const response = await apiClient.get<OverallResponse>(`${SUMMARY_BASE}/overall`, { params })
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching overall:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch overall performance' }
    }
  },

  // Get hourly comparison data (yesterday vs today)
  async getHourly(): Promise<ApiResponse<HourlySummaryResponse>> {
    try {
      const response = await apiClient.get<HourlySummaryResponse>(`${SUMMARY_BASE}/hourly`)
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('Error fetching hourly data:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch hourly data' }
    }
  },
}

export default summaryApi
